/**
 * The lawn builder's lawns (owner idea, 2026-10-02; lm-ocm.6): lawns the player makes, kept in the
 * save. A lawn is the same thing the campaign plays (a level file), drawn in ASCII rows. Before it
 * can be played it is checked: the greedy solver must clear it cleanly with at least one mower,
 * and records each such mower's par. Custom lawns pay no coins and earn no stars, so building a
 * lawn is never a way to farm.
 */
import { CAMPAIGN_LEVELS } from '../content/levels/campaign';
import { loadLevel, type LevelDef, type LevelFile } from '../content/levelSchema';
import { BIOMES, biomeForLevel, type BiomeId } from '../content/biomes';
import { MOWER_IDS, MOWERS, type MowerId } from '../content/mowers';
import { TILE_BY_ID, TILES, type TileId } from '../content/tiles';
import { parseAsciiMap } from '../content/asciiMap';
import { solveGreedy } from '../gen/solver/greedyPlanner';
import type { CritterKind } from '../sim/critters';
import { isSolid, needsMowing } from '../sim/grid';
import { campaignLevelId } from './economy';
import type { Save } from './save/schema';
import {
  BUILDER_CRITTERS,
  BUILDER_PICKUPS,
  type BuilderPickup,
  type CustomLawn,
} from './save/customLawnSchema';

export { BUILDER_CRITTERS, BUILDER_PICKUPS, type BuilderPickup, type CustomLawn };

/** How big a lawn can be: as wide as the screen at most, and a few screens tall. */
export const LAWN_SIZE = { minW: 4, maxW: 12, minH: 4, maxH: 24 } as const;
/** How many lawns the builder keeps. */
export const MAX_CUSTOM_LAWNS = 24;

/** Tiles a boss sets up that a lawn of your own has no use for. */
const NOT_BUILDABLE: ReadonlySet<TileId> = new Set<TileId>([
  'judgesTable',
  'showLawn',
  'pen',
  'penGate',
  'roboDock',
  'log',
]);

/** The tiles a lawn may be painted with (grass first). */
export const BUILDER_TILES: readonly TileId[] = TILES.map((t) => t.id).filter(
  (id) => !NOT_BUILDABLE.has(id),
);

// ── Pieces ───────────────────────────────────────────────────────────────────────────────────

/** Something the builder can place: a tile, a pickup or a critter. */
export type Piece =
  | { kind: 'tile'; id: TileId }
  | { kind: 'pickup'; id: BuilderPickup }
  | { kind: 'critter'; id: CritterKind };

export const pieceKey = (p: Piece): string => `${p.kind}:${p.id}`;

/** Every piece, in palette order: tiles, then pickups, then critters. */
export const ALL_PIECES: readonly Piece[] = [
  ...BUILDER_TILES.map((id): Piece => ({ kind: 'tile', id })),
  ...BUILDER_PICKUPS.map((id): Piece => ({ kind: 'pickup', id })),
  ...BUILDER_CRITTERS.map((id): Piece => ({ kind: 'critter', id })),
];

/**
 * The first campaign lawn each piece appears on (by piece key): a piece is met once that lawn is
 * cleared. Pieces on no campaign lawn are absent (grass is always met).
 */
export const FIRST_MET: ReadonlyMap<string, number> = (() => {
  const out = new Map<string, number>([[pieceKey({ kind: 'tile', id: 'grass' }), 0]]);
  const note = (key: string, lawn: number) => {
    if (!out.has(key)) out.set(key, lawn);
  };
  const lawns = [...CAMPAIGN_LEVELS].sort((a, b) => (a.level ?? 0) - (b.level ?? 0));
  for (const l of lawns) {
    const n = l.level ?? 0;
    for (const id of l.art) note(`tile:${id}`, n);
    for (const p of l.sim.pickups ?? []) note(`pickup:${p.kind}`, n);
    for (const c of l.sim.critters ?? []) note(`critter:${c.kind}`, n);
  }
  return out;
})();

/** Whether the player has met a piece in the campaign (cleared the first lawn it is on). */
export function pieceMet(save: Readonly<Save>, p: Piece): boolean {
  const lawn = FIRST_MET.get(pieceKey(p));
  if (lawn === undefined) return false;
  return lawn === 0 || ((save.levels[campaignLevelId(lawn)]?.stars ?? 0) & 1) !== 0;
}

// ── Making and editing ───────────────────────────────────────────────────────────────────────

/** A copy of a lawn without its check (any edit clears it). */
function unchecked(l: Readonly<CustomLawn>): CustomLawn {
  const copy: CustomLawn = { ...l };
  delete copy.par;
  return copy;
}

/** A fresh lawn: all grass, the mower in the bottom-left corner facing north. */
export function newCustomLawn(save: Readonly<Save>, width = 8, height = 10): CustomLawn {
  const used = save.customLawns.map((l) => Number(l.id.slice(3)));
  const id = `my-${Math.max(0, ...used) + 1}`;
  const rows = Array.from({ length: height }, (_, y) =>
    y === height - 1 ? 'S' + '.'.repeat(width - 1) : '.'.repeat(width),
  );
  return { id, name: 'My Lawn', biome: 'backyard', rows, startDir: 'N', pickups: [], critters: [] };
}

/** The lawn's size. */
export const lawnSize = (l: Readonly<CustomLawn>) => ({
  width: l.rows[0]?.length ?? 0,
  height: l.rows.length,
});

/**
 * Resizes a lawn, keeping its bottom-left corner where it is (new rows and columns are grass);
 * the start, pickups and critters that fall off are moved back on or dropped.
 */
export function resizeLawn(l: Readonly<CustomLawn>, width: number, height: number): CustomLawn {
  const w = Math.max(LAWN_SIZE.minW, Math.min(LAWN_SIZE.maxW, width));
  const h = Math.max(LAWN_SIZE.minH, Math.min(LAWN_SIZE.maxH, height));
  const { height: oldH } = lawnSize(l);
  const dy = h - oldH; // rows added (or removed) at the top
  const rows = Array.from({ length: h }, (_, y) => {
    const old = l.rows[y - dy] ?? '';
    return (old.replace('S', '.') + '.'.repeat(w)).slice(0, w);
  });
  // The start: where it was if it is still on the lawn, else the bottom-left corner.
  const start = startOf(l);
  const sx = Math.min(start.x, w - 1);
  const sy = Math.max(0, Math.min(h - 1, start.y + dy));
  rows[sy] = rows[sy]!.slice(0, sx) + 'S' + rows[sy]!.slice(sx + 1);
  const keep = <T extends { x: number; y: number }>(list: readonly T[]) =>
    list
      .map((p) => ({ ...p, y: p.y + dy }))
      .filter((p) => p.x < w && p.y >= 0 && p.y < h && !(p.x === sx && p.y === sy));
  const rest = unchecked(l);
  return { ...rest, rows, pickups: keep(l.pickups), critters: keep(l.critters) };
}

/** Where the mower starts. */
export function startOf(l: Readonly<CustomLawn>): { x: number; y: number } {
  for (const [y, row] of l.rows.entries()) {
    const x = row.indexOf('S');
    if (x >= 0) return { x, y };
  }
  return { x: 0, y: l.rows.length - 1 };
}

/** The tile painted at (x, y) (the start is grass). */
export function tileAt(l: Readonly<CustomLawn>, x: number, y: number): TileId {
  const ch = l.rows[y]?.[x];
  if (ch === undefined || ch === 'S') return 'grass';
  return TILES.find((t) => t.char === ch)?.id ?? 'grass';
}

/**
 * Places a piece at (x, y): a tile repaints the ground (and clears a pickup or critter that can no
 * longer stand there); a pickup or critter replaces whatever stood on that tile. Never on the
 * start. Any edit clears the check.
 */
export function placePiece(l: Readonly<CustomLawn>, p: Piece, x: number, y: number): CustomLawn {
  const { width, height } = lawnSize(l);
  const s = startOf(l);
  const rest = unchecked(l);
  if (x < 0 || y < 0 || x >= width || y >= height || (x === s.x && y === s.y)) return l;
  const off = <T extends { x: number; y: number }>(list: readonly T[]) =>
    list.filter((q) => q.x !== x || q.y !== y);
  if (p.kind === 'tile') {
    const ch = TILE_BY_ID[p.id].char;
    const rows = l.rows.map((row, ry) =>
      ry === y ? row.slice(0, x) + ch + row.slice(x + 1) : row,
    );
    const blocked = isSolid(TILE_BY_ID[p.id].kind);
    return {
      ...rest,
      rows,
      pickups: blocked ? off(l.pickups) : [...l.pickups],
      critters: blocked ? off(l.critters) : [...l.critters],
    };
  }
  // Pickups and critters stand on open ground only.
  if (isSolid(TILE_BY_ID[tileAt(l, x, y)].kind)) return l;
  if (p.kind === 'pickup') {
    return {
      ...rest,
      pickups: [...off(l.pickups), { kind: p.id, x, y }],
      critters: off(l.critters),
    };
  }
  return { ...rest, critters: [...off(l.critters), { kind: p.id, x, y }], pickups: off(l.pickups) };
}

/** Clears (x, y) back to grass, with nothing on it. */
export function erase(l: Readonly<CustomLawn>, x: number, y: number): CustomLawn {
  const out = placePiece(l, { kind: 'tile', id: 'grass' }, x, y);
  return {
    ...out,
    pickups: out.pickups.filter((q) => q.x !== x || q.y !== y),
    critters: out.critters.filter((q) => q.x !== x || q.y !== y),
  };
}

/** Moves the start to (x, y) (open ground only); on the start already, turns it a quarter. */
export function moveStart(l: Readonly<CustomLawn>, x: number, y: number): CustomLawn {
  const s = startOf(l);
  const rest = unchecked(l);
  if (x === s.x && y === s.y) {
    const turn = { N: 'E', E: 'S', S: 'W', W: 'N' } as const;
    return { ...rest, startDir: turn[l.startDir] };
  }
  const { width, height } = lawnSize(l);
  if (x < 0 || y < 0 || x >= width || y >= height) return l;
  const here = TILE_BY_ID[tileAt(l, x, y)];
  if (isSolid(here.kind)) return l;
  const rows = l.rows.map((row, ry) => {
    let r = ry === s.y ? row.slice(0, s.x) + '.' + row.slice(s.x + 1) : row;
    if (ry === y) r = r.slice(0, x) + 'S' + r.slice(x + 1);
    return r;
  });
  const off = <T extends { x: number; y: number }>(list: readonly T[]) =>
    list.filter((q) => q.x !== x || q.y !== y);
  return { ...rest, rows, pickups: off(l.pickups), critters: off(l.critters) };
}

// ── Playing ──────────────────────────────────────────────────────────────────────────────────

/** The lawn as a level file: the mowers that clear it (any, before it is checked). */
export function customLevelFile(l: Readonly<CustomLawn>): LevelFile {
  const cleared = Object.keys(l.par ?? {}) as MowerId[];
  return {
    id: l.id,
    name: l.name,
    biome: l.biome,
    startDir: l.startDir,
    mowerRule:
      cleared.length > 0 ? { kind: 'only', mowers: cleared } : { kind: 'minTier', tier: 0 },
    rows: [...l.rows],
    pickups: l.pickups.map((p) => ({ ...p })),
    critters: l.critters.map((c) => ({ ...c })),
    ...(l.par ? { par: { ...l.par } } : {}),
  };
}

export function customLevel(l: Readonly<CustomLawn>): LevelDef {
  return loadLevel(customLevelFile(l));
}

/** What a check found: par per mower that clears it, and anything wrong with it. */
export interface LawnCheck {
  par: Partial<Record<MowerId, number>>;
  problems: string[];
}

/**
 * Checks a lawn: it must have grass to mow, and the greedy solver must clear it cleanly with at
 * least one mower (the same check as every campaign lawn). Records par for each mower that does.
 */
export function checkLawn(l: Readonly<CustomLawn>): LawnCheck {
  const problems: string[] = [];
  // (The start is always grass: something else must want mowing.)
  if (parseAsciiMap(l.rows).tiles.filter(needsMowing).length <= 1) {
    problems.push('Nothing to mow yet.');
    return { par: {}, problems };
  }
  let def: LevelDef;
  try {
    def = customLevel({ ...l, par: undefined });
  } catch (e) {
    problems.push(e instanceof Error ? e.message : String(e));
    return { par: {}, problems };
  }
  const par: Partial<Record<MowerId, number>> = {};
  for (const id of MOWER_IDS) {
    try {
      const r = solveGreedy(def.sim, MOWERS[id]);
      if (r.won && r.crashes === 0) par[id] = r.ticks;
    } catch {
      // Too wide to fit at the start, or the like: not one of its mowers.
    }
  }
  if (Object.keys(par).length === 0) problems.push('No mower can mow all of it.');
  return { par, problems };
}

/** Saves a lawn into the save (replacing the one with its id), newest first. */
export function storeLawn(draft: Save, l: CustomLawn): void {
  draft.customLawns = [l, ...draft.customLawns.filter((x) => x.id !== l.id)].slice(
    0,
    MAX_CUSTOM_LAWNS,
  );
}

export function deleteLawn(draft: Save, id: string): void {
  draft.customLawns = draft.customLawns.filter((x) => x.id !== id);
}

// ── Names ────────────────────────────────────────────────────────────────────────────────────

export const NAME_FIRST = [
  'Sunny',
  'Shady',
  'Breezy',
  'Tidy',
  'Tangled',
  'Secret',
  'Sleepy',
  'Happy',
  'Windy',
  'Golden',
  'Mossy',
  'Quiet',
  'Lucky',
  'Twisty',
  'Grand',
  'Little',
  'Busy',
  'Misty',
  'Merry',
  'Cozy',
] as const;
export const NAME_SECOND = [
  'Backyard',
  'Garden',
  'Meadow',
  'Patch',
  'Lawn',
  'Corner',
  'Green',
  'Field',
  'Hollow',
  'Plot',
  'Paddock',
  'Commons',
  'Glade',
  'Pasture',
  'Square',
  'Acre',
  'Yard',
  'Orchard',
  'Verge',
  'Park',
] as const;

/** A friendly name for a lawn (the builder rerolls instead of typing: always family-friendly). */
export function lawnName(n: number): string {
  const a = NAME_FIRST[Math.abs(n) % NAME_FIRST.length]!;
  const b = NAME_SECOND[Math.abs(Math.floor(n / NAME_FIRST.length) * 7 + n) % NAME_SECOND.length]!;
  return `${a} ${b}`;
}

// ── The builder's shop (economy pass 2, owner 2026-10-05; lm-ma4.16) ─────────────────────────

/**
 * The builder and its piece kits are coin unlocks, priced to be bought early but cheaper as the
 * campaign goes on: 10% off for each zone cleared, down to `floor` of the price. A kit is a
 * zone's pieces (the ones its lawns introduce); it is 30% off once every one of them is met, and
 * can be bought before at full price. The builder comes with the Backyard's kit.
 */
export const BUILDER_SHOP = {
  /** The builder: on sale once this lawn is cleared (the Farm's big goal). */
  opensAfter: 40,
  price: 600,
  kitPrice: 300,
  discountPerZone: 0.1,
  floor: 0.4,
  metDiscount: 0.3,
} as const;

/** Zones cleared: their finale (boss) lawn beaten. */
export function zonesCleared(save: Readonly<Save>): number {
  return BIOMES.filter((b) => ((save.levels[campaignLevelId(b.levels[1])]?.stars ?? 0) & 1) !== 0)
    .length;
}

/** A price after the zone discount (never below the floor). */
function discounted(save: Readonly<Save>, price: number): number {
  const cut = Math.min(1 - BUILDER_SHOP.floor, BUILDER_SHOP.discountPerZone * zonesCleared(save));
  return Math.round(price * (1 - cut));
}

/** The builder's price now, or null once it is owned. */
export function builderPrice(save: Readonly<Save>): number | null {
  return save.builder.owned ? null : discounted(save, BUILDER_SHOP.price);
}

/** The builder's lowest price, after every zone (for "drops to ..." on its card). */
export const BUILDER_FLOOR_PRICE = Math.round(BUILDER_SHOP.price * BUILDER_SHOP.floor);

/** Whether the builder is on sale (or owned). */
export function builderOpen(save: Readonly<Save>): boolean {
  return (
    save.builder.owned ||
    ((save.levels[campaignLevelId(BUILDER_SHOP.opensAfter)]?.stars ?? 0) & 1) !== 0
  );
}

export function buyBuilder(draft: Save): boolean {
  const price = builderPrice(draft);
  if (price === null || !builderOpen(draft) || draft.coins < price) return false;
  draft.coins -= price;
  draft.builder.owned = true;
  if (!draft.builder.kits.includes('backyard')) draft.builder.kits.push('backyard');
  return true;
}

/** Each zone's kit: the pieces first met on its lawns. */
export const KITS: ReadonlyMap<BiomeId, readonly Piece[]> = new Map(
  BIOMES.map((b) => [
    b.id,
    ALL_PIECES.filter((p) => {
      const lawn = FIRST_MET.get(pieceKey(p));
      return lawn !== undefined && biomeForLevel(Math.max(1, lawn)).id === b.id;
    }),
  ]),
);

/** The zone whose kit has a piece. */
export function kitOf(p: Piece): BiomeId | null {
  for (const [zone, pieces] of KITS)
    if (pieces.some((q) => pieceKey(q) === pieceKey(p))) return zone;
  return null;
}

/** Whether every piece in a zone's kit has been met. */
export function kitMet(save: Readonly<Save>, zone: BiomeId): boolean {
  return (KITS.get(zone) ?? []).every((p) => pieceMet(save, p));
}

/** A kit's price now (zone discount, and cheaper once met), or null once owned. */
export function kitPrice(save: Readonly<Save>, zone: BiomeId): number | null {
  if (save.builder.kits.includes(zone)) return null;
  const met = kitMet(save, zone) ? 1 - BUILDER_SHOP.metDiscount : 1;
  return Math.round(discounted(save, BUILDER_SHOP.kitPrice) * met);
}

export function buyKit(draft: Save, zone: BiomeId): boolean {
  const price = kitPrice(draft, zone);
  if (price === null || !draft.builder.owned || draft.coins < price) return false;
  draft.coins -= price;
  draft.builder.kits.push(zone);
  return true;
}

/** Whether the palette lets a piece be placed: its kit is owned (grass always). */
export function pieceUsable(save: Readonly<Save>, p: Piece): boolean {
  if (p.kind === 'tile' && p.id === 'grass') return true;
  const zone = kitOf(p);
  return zone !== null && save.builder.kits.includes(zone);
}
